/**
 * PATH: apps/sam/src/features/email/hooks/useEmailBodyImageInteractions.ts
 * FILE_ID: SAM-EMAIL-HOOK-BODY-IMG-001
 * PURPOSE: Wire click + right-click handlers onto a single ref'd
 *          email-body container, using event delegation so we
 *          never touch the sanitized HTML and never re-render the
 *          body when the lightbox or context menu opens.
 * WHY:     The email body is rendered with
 *          ``dangerouslySetInnerHTML``. Adding interactivity by
 *          editing the HTML string post-sanitize would re-open
 *          the XSS vector that the sanitiser exists to close. Event
 *          delegation on the parent container means *any* `<img>`
 *          (current and future) becomes clickable / right-clickable
 *          for free, with no HTML rewriting.
 * FLOW:
 *   bind(rootRef) attaches:
 *     click       → if target instanceof HTMLImageElement → open lightbox
 *     contextmenu → if target instanceof HTMLImageElement → open menu
 *                                                            (preventDefault)
 * RELATED:
 *   - components/InlineImageLightbox.tsx
 *   - components/InlineImageMenu.tsx
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { InlineImageMenuState } from '../components/InlineImageMenu'

export interface InlineImageTarget {
  src: string
  alt?: string
}

export interface UseEmailBodyImageInteractionsResult {
  /** Attach this ref to the container that hosts the
   *  ``dangerouslySetInnerHTML`` div (NOT the inner div directly —
   *  pass either, both work, but a stable parent re-binds less
   *  often). The ``| null`` matches React 19's stricter
   *  ``RefObject`` typing. */
  bodyRef: React.RefObject<HTMLDivElement | null>
  lightboxImage: InlineImageTarget | null
  closeLightbox: () => void
  menu: InlineImageMenuState | null
  closeMenu: () => void
  /** Imperative open APIs the lightbox and right-click menu use to
   *  promote each other (e.g. menu's "Open larger" → opens the
   *  lightbox, lightbox's "Mark up" → opens the markup drawer via
   *  the supplied callback). */
  openLightbox: (src: string, alt?: string) => void
}

/** ``emailId`` is the email currently visible in the side pane.
 *  Pass ``null`` if the operator is on a list view with no email
 *  open — the menu's "Reply with this image" entry collapses to
 *  "New email with this image" in that case. */
export function useEmailBodyImageInteractions(
  emailId: string | null,
): UseEmailBodyImageInteractionsResult {
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const [lightboxImage, setLightboxImage] = useState<InlineImageTarget | null>(null)
  const [menu, setMenu] = useState<InlineImageMenuState | null>(null)

  const openLightbox = useCallback((src: string, alt?: string) => {
    setLightboxImage({ src, alt })
  }, [])

  useEffect(() => {
    const root = bodyRef.current
    if (!root) return

    const onClick = (e: MouseEvent) => {
      const t = e.target
      if (!(t instanceof HTMLImageElement)) return
      if (!isReadableImage(t)) return
      e.preventDefault()
      e.stopPropagation()
      setLightboxImage({ src: t.currentSrc || t.src, alt: t.alt || undefined })
    }

    const onContext = (e: MouseEvent) => {
      const t = e.target
      if (!(t instanceof HTMLImageElement)) return
      if (!isReadableImage(t)) return
      e.preventDefault()
      e.stopPropagation()
      setMenu({
        x: e.clientX,
        y: e.clientY,
        src: t.currentSrc || t.src,
        alt: t.alt || undefined,
        emailId,
      })
    }

    root.addEventListener('click', onClick)
    root.addEventListener('contextmenu', onContext)
    return () => {
      root.removeEventListener('click', onClick)
      root.removeEventListener('contextmenu', onContext)
    }
  }, [emailId])

  return {
    bodyRef,
    lightboxImage,
    closeLightbox: () => setLightboxImage(null),
    menu,
    closeMenu: () => setMenu(null),
    openLightbox,
  }
}

/** Skip 1×1 spacer / tracking pixels and any image with a missing
 *  src. They're rendered for layout / open-rate-tracking but the
 *  operator never wants a "lightbox of a 1px gif" UX. */
function isReadableImage(img: HTMLImageElement): boolean {
  const src = img.currentSrc || img.src
  if (!src) return false
  const w = img.naturalWidth || img.width
  const h = img.naturalHeight || img.height
  if (w > 0 && h > 0 && w <= 2 && h <= 2) return false
  return true
}
